import React from 'react';
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import DataGrid from '../../Component/DataGrid/DataGrid';
import { TableColumn } from 'react-data-table-component';
import { ColumnsFXPoolDetails, FXReportsProps, FXReportsState, IFXReportsDetails, ITableColumns, TabsFXReports, TabsFXReportsNames, TabsFXReportsWidths } from './Interface';
import { SyncContext, SyncContextType } from '../../Contexts/BatchSyncContext';
import UserAndPermissionService from '../../Services/UserAndPermissionService';
import { FXReportsPermissions } from '../../Component/Permissions/PermissionsInterface.';
import FXReportsService from '../../Services/FXReportsService';
import { ISelectedDateRangeString, ITabData } from '../../Component/DataGrid/Interface';
import { clearLocalStorage, getColumnsFXPoolDetails, getRequestDataForReportsWithDateAndPage, ReportsCommon, Texts } from '../../Component/Reusables/Utility';
import moment from 'moment';
import { formatLimit } from '../../Component/config';
import ReusableDrawer from '../../Component/RightDrawer/ReusableDrawer';
import { fetchRecipientCountryList } from '../../Component/Reusables/Recipient';

class FXReports extends React.Component<FXReportsProps, FXReportsState> {
    static contextType = SyncContext;
    public title = 'FX Reports';
    public isDatePickerNeeded: Record<TabsFXReports, boolean>;
    public tableColumns: ITableColumns = {};
    private _context!: SyncContextType;
    private _tabs = Object.values(TabsFXReports) as TabsFXReports[];

    constructor(props_: FXReportsProps) {
        super(props_);
        let URLParams = new URLSearchParams(window.location.search);
        let emailId = URLParams.get("email")?.toString();
        let fxReportsSettings: FXReportsPermissions = {
            isVisible: true
        }
        let tabData: ITabData[] = [];
        for (let tab of this._tabs) {
            fxReportsSettings[tab] = {
                isVisible: true,
                isDownloadVisible: true
            }
            tabData[tab] = {
                label: (TabsFXReportsNames as any)[TabsFXReports[tab]],
                visible: true,
                isDownloadVisible: true,
                value: tab,
                width: (TabsFXReportsWidths as any)[TabsFXReports[tab]]
            }
        }
        this.state = {
            accessToken: "",
            currentRowsPerPage: 10,
            currentPage: 1,
            paginationReset: false,
            totalRows: 100,
            searchText: "",
            userId: '',
            emailId: emailId ? emailId : '',
            selectedTab: TabsFXReports.Inventory,
            fxReportsSettings,
            data: [],
            csvData: [],
            tabData,
            selectedDateRange: {
                startDate: '',
                endDate: ''
            },
            isDrawerOpen: false,
            appliedFiltersCount: 0,
            countryId: ""
        }
        const listColumnsToHide = [
            ColumnsFXPoolDetails.liquidityPartnerId,
            ColumnsFXPoolDetails.liquidityPartner,
            ColumnsFXPoolDetails.settlementDate,
            ColumnsFXPoolDetails.usdCurrencyRate,
            ColumnsFXPoolDetails.fromCurrencyRate,
            ColumnsFXPoolDetails.toCurrencyRate,
            ColumnsFXPoolDetails.targetCountryCorridor,
            ColumnsFXPoolDetails.proofOfPurchaseLocation,
            ColumnsFXPoolDetails.fromCurrencySymbol,
            ColumnsFXPoolDetails.toCurrencySymbol
        ];
        this.tableColumns[TabsFXReports.Inventory] = getColumnsFXPoolDetails(listColumnsToHide);
        this.tableColumns[TabsFXReports.Profitability] = [
            {
                name: 'Pool ID',
                selector: row => row.poolId,
                sortable: true,
                id: "poolId"
            },
            {
                name: 'Source Currency',
                selector: row => row.sourceCurrency,
                sortable: true,
                id: "sourceCurrency",
                center: true
            },
            {
                name: 'Target Currency',
                selector: row => row.targetCurrency,
                sortable: true,
                id: "targetCurrency",
                center: true
            },
            {
                name: 'Estimated cost (USD)',
                selector: row => formatLimit(row.estimatedCostUsd, Texts.langEnUs, row.currency, row.currencySymbol),
                sortable: true,
                id: "estimatedCostUsd",
                right: true,
                minWidth: '150px'
            },
            {
                name: 'Total cost (USD)',
                selector: row => formatLimit(row.totalCostUsd, Texts.langEnUs, row.currency, row.currencySymbol),
                sortable: true,
                id: "totalCostUsd",
                right: true,
                minWidth: '150px'
            },
            {
                name: 'Corridor',
                selector: row => row.corridor,
                sortable: true,
                id: "corridor",
                center: true
            },
            {
                name: 'Liquidity Partner',
                selector: row => row.liquidityPartner,
                sortable: true,
                id: "liquidityPartner"
            },
            {
                name: 'Payout Partner',
                selector: row => row.payoutPartners,
                sortable: true,
                id: "payoutPartner"
            },
            {
                name: 'Pool Status',
                selector: row => row.status,
                sortable: true,
                id: "status",
                center: true
            },
            {
                name: 'Margin',
                selector: row => formatLimit(row.margin, Texts.langEnUs, row.currency, row.currencySymbol),
                sortable: true,
                id: "margin",
                right: true
            }
        ];
        this.tableColumns[TabsFXReports.PlaceholderAllocation] = [];
        this.tableColumns[TabsFXReports.InventoryAdjustment] = [];
        this.tableColumns[TabsFXReports.PoolDepletion] = [
            {
                name: 'Pool Name',
                selector: row => row.poolName,
                sortable: false,
                omit: true,
                id: "poolName"
            },
            {
                name: 'Trade Date',
                selector: row => moment(row.tradeDate).format(Texts.formatDateTimeDisplay),
                sortable: true,
                id: "tradeDate"
            },
            {
                name: 'Settlement Date',
                selector: row => moment(row.settlementDate).format(Texts.formatDateTimeDisplay),
                sortable: true,
                id: "settlementDate"
            },
            {
                name: 'From Currency',
                selector: row => row.fromCurrency,
                sortable: true,
                id: "fromCurrency",
                center: true
            },
            {
                name: 'To Currency',
                selector: row => row.toCurrency,
                sortable: true,
                id: "toCurrency",
                center: true
            },
            {
                name: 'Exchange Rate',
                selector: row => formatLimit(row.exchangeRate, Texts.langEnUs, row.toCurrency, row.toCurrencySymbol),
                sortable: true,
                id: "exchangeRate",
                right: true
            },
            {
                name: 'Amount',
                selector: row => formatLimit(row.amount, Texts.langEnUs, row.toCurrency, row.toCurrencySymbol),
                sortable: true,
                id: "amount",
                right: true
            },
            {
                name: 'Amount (USD)',
                selector: row => formatLimit(row.amountUSD, Texts.langEnUs, Texts.USD, Texts.SymbolDollar),
                sortable: true,
                id: "amountUSD",
                right: true
            }
        ]
        this.isDatePickerNeeded = {
            [TabsFXReports.Inventory]: true,
            [TabsFXReports.Profitability]: false,
            [TabsFXReports.PlaceholderAllocation]: true,
            [TabsFXReports.InventoryAdjustment]: true,
            [TabsFXReports.PoolDepletion]: true
        };
    }


    public async componentDidMount() {
        try {
            //Yet to add permission for FX report page

            const _accessToken = await loadFromLocalStorage('accessToken');
            const _profile = await loadFromLocalStorage('profile');

            if (!_accessToken || !_profile) {
                clearLocalStorage();
                return;
            }
            const accessToken = _accessToken.toString();
            const userProfile = typeof _profile === 'string' ? JSON.parse(_profile) : _profile;


            this.setState({ accessToken }, async () => {
                try {
                    if (userProfile?.roleId) {
                        const permissionResponseRaw = await UserAndPermissionService.getPermissions(accessToken, userProfile?.roleId);
                        const permissionResponse = await permissionResponseRaw.json();

                        if (!permissionResponse?.permissions?.fxReports?.isVisible) {
                            clearLocalStorage();
                            return;
                        }

                        const permissions = permissionResponse.permissions

                        this._context.dispatch({
                            type: "SET_PERMISSION", payload: {
                                permissions
                            },
                        });

                        const settings = permissionResponse.permissions.fxReports;
                        let fxReportsSettings: FXReportsPermissions = {
                            isVisible: settings.isVisible
                        };
                        let selectedTab = -1;
                        let tabData = this.state.tabData;
                        for (let tab of this._tabs) {
                            if (settings[`${Texts.isVisible}${TabsFXReports[tab]}`] && selectedTab === -1) {
                                selectedTab = tab;
                            }
                            let isVisible = settings[`${Texts.isVisible}${TabsFXReports[tab]}`];
                            let isDownloadVisible = settings[`${Texts.isDownloadVisible}${TabsFXReports[tab]}`];
                            fxReportsSettings[tab] = {
                                isVisible,
                                isDownloadVisible
                            }
                            tabData[tab].visible = isVisible;
                            tabData[tab].isDownloadVisible = isDownloadVisible;
                        }
                        if (selectedTab === -1) {
                            clearLocalStorage();
                            return;
                        }

                        this.setState({ fxReportsSettings, selectedTab, tabData }, () => {
                            this.fetchReportData()
                        });

                    } else {
                        this.fetchReportData()
                    }
                } catch (error) {
                    console.error("Error fetching permissions:", error);
                }
            });

        } catch (error) {
            console.error("Error in componentDidMount:", error);
            clearLocalStorage();
        }
    }

    public handleTabChange(event: React.ChangeEvent<{}>, newValue: number) {
        // Create a new URLSearchParams object from the current URL
        const URLParams = new URLSearchParams(window.location.search);

        URLParams.delete("email");

        // Update the URL without reloading the page
        const newUrl = `${window.location.pathname}${URLParams.toString()}`;
        window.history.replaceState(null, "", newUrl);

        this.setState(prevState => ({
            selectedTab: newValue,
            searchText: "",
            emailId: "",
            currentPage: 1,
            paginationReset: !prevState.paginationReset,
            selectedDateRange: {
                startDate: '',
                endDate: ''
            },
            data: [],
            csvData: [],
            appliedFiltersCount: 0
        }), () => this.fetchReportData());
    };

    public getTableColumns(): TableColumn<IFXReportsDetails>[] {
        return this.tableColumns[this.state.selectedTab] as TableColumn<IFXReportsDetails>[];
    };

    public getIsDatePickerNeeded(): boolean {
        return this.isDatePickerNeeded[this.state.selectedTab];
    };

    public getsearchPlaceholder(): string {
        return ((TabsFXReportsNames as any)[TabsFXReports[this.state.selectedTab]]).toLowerCase();
    };

    public getCSVFileName(): string {
        return `${this.title}-${(TabsFXReportsNames as any)[TabsFXReports[this.state.selectedTab]]}`;
    };

    public getCSVData(startDate: string | null, endDate: string | null) {
        let postData: any = {
            startDate, endDate
        }
        if (this.state.searchText) {
            postData.searchText = this.state.searchText
        }
        FXReportsService.getReportDetails(this.state.accessToken, postData, TabsFXReports[this.state.selectedTab], true).then(tr => {
            if (tr.ok) {
                tr.json().then(resp => {
                    this.setState({
                        csvData: resp.data
                    })
                });
            }
        });
    }

    public isDownloadVisble(): boolean | undefined {
        return this.state.fxReportsSettings ? this.state.fxReportsSettings[this.state.selectedTab]?.isDownloadVisible : false;
    };

    updateData = () => {
        this.fetchReportData();
    };

    fetchDropdownOptions = () => {
        return fetchRecipientCountryList(this.state.accessToken);
    };

    handleDrawerApply = (filters: { selectedId: string; startDate: string | null; endDate: string | null }) => {
        const { selectedId, startDate, endDate } = filters;

        this.setState(
            {
                countryId: selectedId || "",
                selectedDateRange: {
                    startDate: startDate || null,
                    endDate: endDate || null,
                },
                currentPage: 1,
            },
            () => {
                this.fetchReportData();

            }
        );
    };

    public render() {
        const { searchText, totalRows, selectedTab, paginationReset } = this.state

        return (
            <div className="body-sub-container table-container">
                {selectedTab !== null && (
                    <DataGrid
                        title={this.title}
                        search={this.getsearchPlaceholder()}
                        //isDatePickerNeeded={this.getIsDatePickerNeeded()}
                        isClearDateOnSearch={this.getIsDatePickerNeeded()}
                        defaultSearch={searchText}
                        columns={this.getTableColumns()}
                        data={this.state.data}
                        csvData={this.state.csvData}
                        csvFileName={this.getCSVFileName()}
                        onSearch={ReportsCommon.onSearchReportWithDateFilter.bind(this)}
                        onApply={ReportsCommon.onApplyDateFilter.bind(this)}
                        onChangePage={ReportsCommon.onReportPageChange.bind(this)}
                        onChangeRowsPerPage={ReportsCommon.onReportRowPerPageChange.bind(this)}
                        pagination
                        paginationTotalRows={totalRows}
                        exportCsv={this.isDownloadVisble()}
                        selectedTab={selectedTab}
                        onTabChange={this.handleTabChange.bind(this)}
                        tabData={this.state.tabData}
                        paginationDefaultPage={1}
                        paginationResetDefaultPage={paginationReset}
                        isDrawerRequired={true}
                        isDrawerOpen={() => this.setState({ isDrawerOpen: true })}
                        appliedFiltersCount={this.state.appliedFiltersCount}
                        selectedCountry={this.state.countryId}
                    />
                )}

                <ReusableDrawer
                    open={this.state.isDrawerOpen}
                    onClose={() => this.setState({ isDrawerOpen: false })}
                    onApply={this.handleDrawerApply}
                    fetchDropdownOptions={this.fetchDropdownOptions}
                    defaultValue={this.state.countryId}
                    onFiltersCountChange={(count) => this.setState({ appliedFiltersCount: count })}
                    defaultStartDate={this.state.selectedDateRange.startDate}
                    defaultEndDate={this.state.selectedDateRange.endDate}
                    showDateRange={this.getIsDatePickerNeeded()}
                    showTxnStatusFilter={false}
                />
            </div>
        )
    }

    private fetchReportData() {
        FXReportsService.getReportDetails(this.state.accessToken, getRequestDataForReportsWithDateAndPage(this.state), TabsFXReports[this.state.selectedTab]).then(response => {
            if (response.ok) {
                response.json().then(resp => {
                    this.setState({
                        data: resp.data,
                        totalRows: resp.totalRecords
                    })
                });
            }
            else {
                throw new Error('Failed to data');
            }
        });
        this.getCSVData(null, null);
    }
}

export default FXReports;